'use client'

import { useState } from "react";
import UserSearch from "./UserSearch";
import UserScroll from "./UserScroll";
import UserActions from "./UserActions";
import { User } from "@/types/user";

import LinkUserToCompany from "./LinkUserToCompany";
import Modal from "@/components/Modal/Modal";
import CreateUserModal from "./CreateUserModal";
import UserRolesList from "./UserRolesList/UserRolesList";
import {Companies} from "@/types/companies";


const UserListView = () => {
    const [filter, setFilter] = useState('');
    const [selectedUser, setSelectedUser] = useState<User>();
    const [isUserDetailsModalOpen, setIsUserDetailsModalOpen] = useState<boolean>(false);
    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const handleOnSearch = (value: string) => {
      setFilter(value);
    }

    const handleOnSelectedUserChange = (user: User) => {
        setSelectedUser(user)
        setIsUserDetailsModalOpen(true)
    }

    const handleCloseCreateUserModal = () => {
        setIsCreateUserModalOpen(false);
    }

    const handleCloseUserDetailsModal = () => {
        setIsUserDetailsModalOpen(false);
        setSelectedUser(undefined);
    }

    const handleUserCreated = () => {
        setRefreshKey(prev => prev + 1);
    }

    const handleUserCompaniesChange = (companies: Companies[]) => {
        setSelectedUser(currentUser => {
            if (!currentUser) return currentUser

            return {
                ...currentUser,
                companies,
                company_ids: companies.map(company => company.id),
                company_id: companies.some(company => company.id === currentUser.company_id)
                    ? currentUser.company_id
                    : '',
                metadata: {
                    ...currentUser.metadata,
                    companyIds: companies.map(company => company.id),
                },
            }
        })
        setRefreshKey(prev => prev + 1)
    }

    return (
        <div>
            <UserSearch
                onSearch={handleOnSearch}
                onAddUserClick={() => setIsCreateUserModalOpen(true)}
            />
            <div className="mt-xxl h-full w-full">
                <UserScroll
                    key={refreshKey}
                    filter={filter}
                    onSelectedUserChange={handleOnSelectedUserChange}
                />
            </div>

            <Modal
                isOpen={isUserDetailsModalOpen}
                onClose={handleCloseUserDetailsModal}
                title="Manage User"
                className="!p-0"
                closeOnOutsideClick={false}
            >
                {selectedUser && (
                    <div className="w-[850px] max-w-[calc(100vw-80px)]">
                        <div className="border-b border-gray-200 bg-gray-50">
                            <div className="flex flex-col gap-xs px-xl pb-xl pt-xl">
                                <div className="text-title-xl capitalize text-gray-900">
                                    {selectedUser.full_name || 'Unnamed User'}
                                </div>
                                <div className="inline-flex items-center gap-sm text-text-md text-gray-400">
                                    {selectedUser.email || 'No email address'}
                                </div>
                                <UserActions user={selectedUser} />
                            </div>
                        </div>
                        <div className="flex flex-col gap-xxl p-xl">
                            <UserRolesList user={selectedUser} />
                            <LinkUserToCompany
                                selectedUser={selectedUser}
                                onCompaniesChange={handleUserCompaniesChange}
                            />
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                isOpen={isCreateUserModalOpen}
                onClose={handleCloseCreateUserModal}
                title="Add User"
                closeOnOutsideClick={false}
            >
                {isCreateUserModalOpen &&
                    <CreateUserModal
                        onClose={handleCloseCreateUserModal}
                        onUserCreated={handleUserCreated}
                    />
                }
            </Modal>
        </div>
    )
}

export default UserListView;
